<template>
  <div class="spm-backdrop" @click.self="onClose">
    <div class="spm-modal" role="dialog" aria-modal="true">
      <header class="spm-header">
        <div>
          <div class="spm-eyebrow">Send to patient</div>
          <div class="spm-title" v-if="patient">
            {{ patient.fName }} {{ patient.lName }}
          </div>
        </div>
        <button class="spm-close" @click="onClose" aria-label="Close">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="spm-body">
        <div v-if="error" class="spm-error">{{ error }}</div>

        <div v-if="generating" class="spm-loading">
          <i class="fa fa-circle-o-notch fa-spin"></i>
          <span>Generating short link…</span>
        </div>

        <template v-else-if="fullUrl">
          <div class="spm-section">
            <div class="spm-section-label">Patient link</div>
            <div class="spm-link-row">
              <input ref="urlInput" :value="fullUrl" class="spm-link-input" readonly @click="onSelectAll" />
              <button
                class="spm-btn-primary"
                :class="{ 'spm-btn-success': copied }"
                @click="onCopy"
              >
                <i class="fa" :class="copied ? 'fa-check' : 'fa-clipboard'"></i>
                {{ copied ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <p class="spm-hint" v-if="reused">
              This patient already has a link from a previous session — reusing it so they don't get duplicates.
            </p>
            <p class="spm-hint" v-else>
              The link works on any device. Patient sees the plan but can't edit it.
            </p>
            <!-- Copy-only practice (no Teamio texting or verified sender): name the
                 workflow outright instead of leaving them staring at two disabled
                 tiles. This IS their send path, not a fallback. -->
            <p class="spm-hint spm-hint-copyonly" v-if="copyOnly">
              <i class="fa fa-info-circle"></i>
              Paste this into your texting tool to send it to {{ patientFirstName }}.
            </p>
          </div>

          <!-- Slice 11 Phase B — Consent forms bundling -->
          <!-- Slice 10a #4 — also require at least one procedure on the
               plan. Otherwise the section renders empty (no chips, no
               warning) on a zero-procedure plan, which reads as broken UI. -->
          <div class="spm-section" v-if="!sentResult && hasConsentMappingsConfigured && planProcedures.length > 0">
            <label class="spm-consent-toggle">
              <input
                type="checkbox"
                v-model="bundleConsentForms"
                :disabled="sending || bundleSaving || (resolvedConsentForms.length === 0 && !bundleConsentForms)"
                @change="onToggleBundleConsentForms"
              />
              <span class="spm-consent-label">
                Also send consent forms for procedures on this plan
                <i v-if="bundleSaving" class="fa fa-circle-o-notch fa-spin spm-consent-saving"></i>
              </span>
            </label>

            <!-- Slice 10a #11 — bundle save error visibility. Without
                 surfacing the failure, a silent network blip leaves the
                 toggle out of sync with the backend; patient view then
                 renders the wrong bundle. We revert the local toggle on
                 error (see onToggleBundleConsentForms) so the UI matches
                 what's actually persisted. -->
            <div v-if="bundleSaveError" class="spm-consent-error">
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ bundleSaveError }}</span>
              <button class="spm-consent-error-dismiss" type="button" @click="bundleSaveError = null" aria-label="Dismiss">
                <i class="fa fa-times"></i>
              </button>
            </div>

            <div v-if="bundleConsentForms && resolvedConsentForms.length > 0" class="spm-consent-preview">
              <div class="spm-consent-preview-label">Will include:</div>
              <div class="spm-consent-chips">
                <span
                  v-for="f in resolvedConsentForms"
                  :key="f.formIDForWeb"
                  class="spm-consent-chip"
                >
                  <i class="fa fa-file-text-o"></i>
                  {{ f.formName }}
                </span>
              </div>
            </div>

            <div v-if="bundleConsentForms && resolvedConsentForms.length === 0" class="spm-consent-empty">
              <i class="fa fa-info-circle"></i>
              No consent forms map to the procedures on this plan. Configure mappings in Settings &rarr; Consent Forms.
            </div>

            <div v-if="bundleConsentForms && unmappedCodes.length > 0" class="spm-consent-warn">
              <i class="fa fa-exclamation-triangle"></i>
              <div>
                <strong>{{ unmappedCodes.length }}</strong>
                {{ unmappedCodes.length === 1 ? 'procedure doesn\'t' : 'procedures don\'t' }}
                have consent forms configured ({{ unmappedCodesPreview }}). Send proceeds &mdash; only mapped forms bundle.
                <a href="#" @click.prevent="goToConsentSettings">Configure</a>
              </div>
            </div>
          </div>

          <!-- Photo Attachments — the last look before this goes out. The TC
               sees exactly which photos the patient will get, so a photo of
               the wrong teeth is caught here rather than by the patient.
               Hidden when the patient has no photos at all: an empty section
               would just be a toggle with nothing behind it. -->
          <div class="spm-section" v-if="!sentResult && patientPhotoCount > 0">
            <label class="spm-consent-toggle">
              <input
                type="checkbox"
                v-model="includePhotos"
                :disabled="sending || photosSaving"
                @change="onToggleIncludePhotos"
              />
              <span class="spm-consent-label">
                Include photos with this plan
                <i v-if="photosSaving" class="fa fa-circle-o-notch fa-spin spm-consent-saving"></i>
              </span>
            </label>

            <div v-if="photosSaveError" class="spm-consent-error">
              <i class="fa fa-exclamation-triangle"></i>
              <span>{{ photosSaveError }}</span>
              <button class="spm-consent-error-dismiss" type="button" @click="photosSaveError = null" aria-label="Dismiss">
                <i class="fa fa-times"></i>
              </button>
            </div>

            <div v-if="includePhotos && planPhotos.length > 0" class="spm-consent-preview">
              <div class="spm-consent-preview-label">Will include:</div>
              <div class="spm-photo-strip">
                <div v-for="p in planPhotos" :key="p.photoId" class="spm-photo-item">
                  <img :src="p.url" :alt="p.fileName" class="spm-photo-thumb" />
                  <div class="spm-photo-teeth" v-if="(p.teeth || []).length > 0">
                    {{ (p.teeth || []).map((t) => '#' + t).join(' ') }}
                  </div>
                  <div class="spm-photo-teeth spm-photo-teeth-any" v-else>All plans</div>
                </div>
              </div>
            </div>

            <!-- The patient HAS photos, but none of them are tagged with a
                 tooth on this plan. Worth saying out loud: from the TC's side
                 "I attached photos" and "the patient will see photos" feel
                 like the same thing, and here they aren't. -->
            <div v-if="includePhotos && planPhotos.length === 0" class="spm-consent-empty">
              <i class="fa fa-info-circle"></i>
              None of this patient's {{ patientPhotoCount }} photos are tagged with a tooth on this
              plan, so none will be included. Retag them under <strong>Attach Photos</strong>.
            </div>
          </div>

          <div class="spm-section" v-if="!sentResult">
            <div class="spm-section-label">Send via</div>
            <div class="spm-channels">
              <!-- Slice 10a #5 — disable a channel when the patient's
                   contact field is missing. Without this, TC selects SMS,
                   backend rejects with 400 "No phone number on file," and
                   the modal stays on SMS forcing TC to manually switch. -->
              <!-- Tiles stay VISIBLE when unavailable, only disabled: an office
                   that can't text still needs to see that Copy link is the path,
                   and a hidden control teaches nothing. -->
              <button
                class="spm-channel"
                :class="{ 'spm-channel-active': activeChannel === 'sms' }"
                :disabled="!canSendSms"
                :title="smsDisabledReason"
                @click="activeChannel = 'sms'"
              >
                <i class="fa fa-mobile"></i>
                <span class="spm-channel-name">SMS</span>
                <span class="spm-channel-sub">{{ smsChannelSub }}</span>
              </button>
              <button
                class="spm-channel"
                :class="{ 'spm-channel-active': activeChannel === 'email' }"
                :disabled="!canSendEmail"
                :title="emailDisabledReason"
                @click="activeChannel = 'email'"
              >
                <i class="fa fa-envelope-o"></i>
                <span class="spm-channel-name">Email</span>
                <span class="spm-channel-sub">{{ emailChannelSub }}</span>
              </button>
              <button
                class="spm-channel"
                :class="{ 'spm-channel-active': activeChannel === 'copy' }"
                @click="activeChannel = 'copy'; onCopy()"
              >
                <i class="fa fa-clipboard"></i>
                <span class="spm-channel-name">Copy link</span>
                <span class="spm-channel-sub">Paste anywhere</span>
              </button>
            </div>
          </div>

          <!-- SMS form -->
          <div v-if="activeChannel === 'sms' && !sentResult" class="spm-section">
            <div class="spm-field">
              <label>Phone number</label>
              <input
                v-model="smsRecipient"
                class="spm-input"
                placeholder="+1 555 123 4567"
                :disabled="sending"
              />
            </div>
            <div class="spm-field">
              <label>
                Message preview
                <span class="spm-charcount" :class="{ warn: smsBodyResolved.length > 160 }">
                  {{ smsBodyResolved.length }} / 160
                </span>
              </label>
              <textarea
                ref="smsTextarea"
                v-model="smsCustomMessage"
                class="spm-input spm-textarea"
                rows="3"
                :placeholder="smsTemplateRaw"
                :disabled="sending"
              ></textarea>
              <p class="spm-hint">
                Leave blank to use your practice's default. Tap to insert:
                <button
                  v-for="v in messageVars"
                  :key="'sms-' + v"
                  type="button"
                  class="spm-var-chip"
                  :disabled="sending"
                  @mousedown.prevent="insertVar(v, 'smsTextarea', 'smsCustomMessage')"
                >{{ v }}</button>
              </p>
              <!-- Shared SMS guidance: live credit counter + warn/block banner + tips.
                   Measures the resolved body (custom message, or the default template). -->
              <SmsGuidance :text="smsBodyResolved || ''" @change="onSmsGuidance" />
            </div>
            <button
              class="spm-btn-primary spm-btn-block"
              :disabled="sending || !smsRecipient.trim() || smsBlocked"
              @click="onSend('sms')"
            >
              <i v-if="sending" class="fa fa-circle-o-notch fa-spin"></i>
              <i v-else class="fa fa-paper-plane"></i>
              {{ sending ? 'Sending…' : 'Send SMS' }}
            </button>
          </div>

          <!-- Email form -->
          <div v-if="activeChannel === 'email' && !sentResult" class="spm-section">
            <!-- Sender disclosure. The office is about to email a patient on the
                 practice's behalf from a teamio.app address — they should see that
                 BEFORE they send, not discover it in a patient's reply. Only rendered
                 for platform-sender sites; own-sender practices send from their own
                 address and need no explanation. -->
            <p v-if="platformSenderNotice" class="spm-sender-note">
              <i class="fa fa-info-circle"></i>
              Sends as <strong>{{ platformSenderNotice.name }} &lt;{{ platformSenderNotice.email }}&gt;</strong>
              <template v-if="platformSenderNotice.replyTo">
                — replies go to <strong>{{ platformSenderNotice.replyTo }}</strong>
              </template>
            </p>
            <div class="spm-field">
              <label>Email address</label>
              <input
                v-model="emailRecipient"
                class="spm-input"
                type="email"
                placeholder="patient@example.com"
                :disabled="sending"
              />
            </div>
            <div class="spm-field">
              <label>Subject</label>
              <input
                v-model="emailCustomSubject"
                class="spm-input"
                :placeholder="emailSubjectRaw"
                :disabled="sending"
              />
            </div>
            <div class="spm-field">
              <label>Body</label>
              <textarea
                ref="emailBodyTextarea"
                v-model="emailCustomBody"
                class="spm-input spm-textarea"
                rows="6"
                :placeholder="emailBodyRaw"
                :disabled="sending"
              ></textarea>
              <p class="spm-hint">
                Leave any field blank to use your practice's default. Tap to insert:
                <button
                  v-for="v in messageVars"
                  :key="'email-' + v"
                  type="button"
                  class="spm-var-chip"
                  :disabled="sending"
                  @mousedown.prevent="insertVar(v, 'emailBodyTextarea', 'emailCustomBody')"
                >{{ v }}</button>
              </p>
            </div>
            <button
              class="spm-btn-primary spm-btn-block"
              :disabled="sending || !emailRecipient.trim()"
              @click="onSend('email')"
            >
              <i v-if="sending" class="fa fa-circle-o-notch fa-spin"></i>
              <i v-else class="fa fa-paper-plane"></i>
              {{ sending ? 'Sending…' : 'Send email' }}
            </button>
          </div>

          <!-- Sent confirmation. "Already sent" first: the server's send-once claim for THIS attempt was already
               held (a re-click of the same attempt after a lost response), so nothing went out now and the
               green "SMS sent" below must not render. -->
          <div v-if="sentResult && sentResult.channel === 'sms' && sentResult.deduped" class="spm-sent spm-sent-info">
            <i class="fa fa-info-circle"></i>
            <div class="spm-sent-text">
              <div class="spm-sent-title">Already sent</div>
              <div class="spm-sent-meta">This exact text was already sent to {{ sentResult.recipient }} a moment ago, so it was not sent again.</div>
            </div>
          </div>

          <div v-else-if="sentResult" class="spm-sent">
            <i class="fa fa-check-circle"></i>
            <div class="spm-sent-text">
              <div class="spm-sent-title">
                {{ sentResult.channel === 'sms' ? 'SMS sent' : 'Email sent' }}
              </div>
              <div class="spm-sent-meta">to {{ sentResult.recipient }}</div>
            </div>
          </div>

          <div class="spm-section" v-if="!sentResult">
            <div class="spm-section-label">Preview</div>
            <a :href="fullUrl" target="_blank" rel="noopener" class="spm-preview-link">
              <i class="fa fa-external-link"></i>
              Open the patient view in a new tab
            </a>
          </div>
        </template>
      </div>

      <footer class="spm-footer">
        <button class="spm-btn" @click="onClose">Done</button>
      </footer>

    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import SmsGuidance from '@/components/SmsGuidance.vue';
import { extractApiError } from '@/utils/apiError';
import { resolvePricingDisplay } from '../utils/resolveDisplay.js';
import { planToothNums, filterPhotosForPlan } from '../utils/photoFilter.js';
import { getTextBlockReason, isTextBlocked } from '@/utils/textConsent.js';

// One id per Send attempt (the chat's own pattern, ConversationContainerV2.vue). The SMS send-once key on the
// server ends with it, so a new click always sends and a re-click of the SAME attempt does not text twice.
function newAttemptNonce() {
  return `txlink-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default {
  name: 'SendPlanModal',
  components: { SmsGuidance },
  props: {
    patient: { type: Object, default: null },
    // Site timezone (GENERAL_TIMEZONE) — passed to the shared text-consent rule so a
    // texted-opt-out date renders in the practice's zone. Optional; degrades to UTC.
    timezone: { type: String, default: '' },
  },
  data() {
    return {
      copied: false,
      copyTimer: null,
      activeChannel: null,
      sending: false,
      sentResult: null,
      sendError: null,
      // New on every mount (the modal is v-if'd by its parents) and rotated after each successful send. NOT
      // rotated on a failed send: the retry reuses it so a "Twilio accepted, response lost" re-click dedups.
      attemptNonce: newAttemptNonce(),
      smsRecipient: '',
      smsCustomMessage: '',
      smsBlocked: false, // set by <SmsGuidance> when the resolved SMS body is over the carrier limit
      lastGuidance: null, // latest <SmsGuidance> payload, for the send-time cost confirm
      emailRecipient: '',
      emailCustomSubject: '',
      emailCustomBody: '',
      // Clickable insert-at-cursor tokens for the SMS/email message box.
      // Backend substitute() (tx-presenter.js sendTxLink) supports exactly these.
      messageVars: ['{patientFirstName}', '{practiceName}', '{planLink}'],
      // Photo Attachments — local mirror of the plan's `showPhotos` flag,
      // seeded in created() and written straight through on change. This is
      // the SAME flag as "Show Photos in Tx Plan" in the Builder right panel
      // and the TC Controls tray, deliberately: a second, send-only switch
      // would let the modal promise something the plan then doesn't show.
      includePhotos: true,
      photosSaving: false,
      photosSaveError: null,
      // Slice 11 Phase B — bundle consent forms toggle. Seeded from
      // sendPlan.persistedBundledFormIDs on modal open (so reopening reflects
      // what's saved on the row). Saves only fire from @change so the seed
      // doesn't bounce back to the backend.
      bundleConsentForms: false,
      // Slice 10a #11 — visibility for the bundle-save round-trip.
      bundleSaving: false,
      bundleSaveError: null,
      // [{formIDForWeb, formName}] — fetched from /get-forms-for-consent-mapping
      // so the chip preview can show real form names. Cached per modal session.
      consentFormsList: [],
    };
  },
  computed: {
    ...mapState('txPresenter', { sendPlan: (s) => s.sendPlan, builder: (s) => s.builder }),
    ...mapGetters('txPresenter', ['settingsDeliveryResolved', 'settingsGeneralResolved', 'settingsConsentResolved', 'siteChannels', 'siteChannelsReady', 'formsPaidFor']),
    fullUrl() { return this.sendPlan.fullUrl; },
    reused() { return this.sendPlan.reused; },
    generating() { return this.sendPlan.generating; },
    error() { return this.sendPlan.error || this.sendError; },

    // Slice 10a #5 — channel-availability flags derived from the patient's
    // contact fields. Trim because OD imports occasionally land whitespace
    // in empty fields.
    // Digit count, not `.trim()`. Open Dental's phone columns are free text and offices
    // type instructions into them — "DO NOT TEXT", "see notes", "call landline". Those
    // are non-empty, so the old check enabled the SMS tile AND pre-filled the recipient
    // box with the literal words, and the send only failed at Twilio (prod 2026-08-11,
    // Tulip Tree: an HTTP 500 that paged on-call). Mirrors conversationsV2's
    // createPatientSmsConversation and the server's own hasDialableDigits.
    hasPatientPhone() {
      return String((this.patient && this.patient.wirelessPhone) || '').replace(/\D/g, '').length >= 10;
    },
    /**
     * Something IS in the phone field, but it isn't a number. Tracked separately from
     * hasPatientPhone so the tile can say "that doesn't look like a number — check Open
     * Dental" instead of "no phone on file", which sends staff looking for a blank field
     * that isn't blank.
     */
    patientPhoneLooksInvalid() {
      const raw = String((this.patient && this.patient.wirelessPhone) || '').trim();
      return !!raw && !this.hasPatientPhone;
    },
    /**
     * The ONE shared opt-out rule (texted STOP, OD Text-OK = No, or a server/HQ block) —
     * src/utils/textConsent.js. Replaces the old inline `txtMsgOk === 2`, which saw only
     * the OD flag and missed the texted-STOP ledger. isTextBlocked normalizes camelCase,
     * so the Tx-Presenter patient's `txtMsgOk`/`smsOptOutAction` keys are read directly.
     * The server enforces this regardless; the tile just stops offering a refused channel.
     */
    patientOptedOutOfText() {
      return isTextBlocked({ patient: this.patient, timezone: this.timezone || '' });
    },
    hasPatientEmail() {
      return !!(this.patient && (this.patient.email || '').trim());
    },

    // Site-level channel entitlement, on top of the per-patient checks above.
    // A Core-plan office has no Teamio texting (they use a competitor product and
    // paste the link), so offering "Send SMS" here just produced a 403 after the
    // click. Two independent reasons a tile can be off — patient has no contact
    // field vs. the practice isn't on that channel — and the sub-label says which,
    // matching the disabledReason idiom already used in PresentDepositPanel.vue.
    //
    // Both default to AVAILABLE until customer.settings has hydrated. "Unknown" is
    // not "denied": telling a paying All-in-One office that texting isn't on their
    // plan is a far worse failure than briefly offering a tile the server will
    // reject with an accurate 403. The server is the real gate either way.
    siteSmsAvailable() {
      if (!this.siteChannelsReady) return true;
      return !!(this.siteChannels && this.siteChannels.sms.available);
    },
    siteEmailAvailable() {
      if (!this.siteChannelsReady) return true;
      return !!(this.siteChannels && this.siteChannels.email.available);
    },
    /**
     * What the PATIENT will see in their inbox — but ONLY when this practice sends
     * through Teamio rather than from its own domain.
     *
     * Why this exists: the resolver has always returned `sender.isPlatform` "so every
     * surface can say which mode it is in rather than silently behaving differently"
     * (lib/resolveChannels.js) — and this surface, the one an office actually uses to
     * send, ignored it. The office could not see that their treatment plans now leave
     * as `practice@mail.teamio.app` instead of their own address. That is the exact
     * shape of the 2026-07-29 regression this design exists to prevent: mail going out
     * as Teamio without the office knowing.
     *
     * Deliberately null for own-sender sites — they send from their own address, so
     * there is nothing to disclose and a banner would only add noise.
     */
    platformSenderNotice() {
      const s = this.siteChannels && this.siteChannels.sender;
      if (!s || !s.isPlatform || !s.email) return null;
      return { name: s.name || 'Your practice', email: s.email, replyTo: s.replyTo || '' };
    },

    canSendSms() { return this.hasPatientPhone && this.siteSmsAvailable && !this.patientOptedOutOfText; },
    canSendEmail() { return this.hasPatientEmail && this.siteEmailAvailable; },

    // The resolver's reason (siteCapability vocabulary): 'sms-disabled' is a WhatsApp-only office,
    // which HAS texting — "not on your plan" would be false there.
    siteSmsOff() {
      return !!(this.siteChannels && this.siteChannels.sms && this.siteChannels.sms.reason === 'sms-disabled');
    },
    smsChannelSub() {
      if (!this.siteSmsAvailable) return this.siteSmsOff ? 'Texting is turned off' : 'Not on your plan';
      if (this.patientOptedOutOfText) return 'Opted out of texts';
      if (this.patientPhoneLooksInvalid) return 'Number looks invalid';
      return this.hasPatientPhone ? 'Text the link' : 'No phone on file';
    },
    emailChannelSub() {
      if (!this.siteEmailAvailable) return 'Not set up';
      return this.hasPatientEmail ? 'Email the link' : 'No email on file';
    },
    smsDisabledReason() {
      if (!this.siteSmsAvailable && this.siteSmsOff) return 'Texting is turned off for this office — email the link, or use Copy link and send it on WhatsApp.';
      if (!this.siteSmsAvailable) return "Text messaging isn't included in your Teamio plan — use Copy link and send it from your texting tool.";
      // Consent before reachability: if they said no, the state of the phone field is
      // beside the point. The shared rule renders the exact sentence the backend would on
      // a refusal (texted STOP, OD Text-OK = No, or a server/HQ block), so staff read the
      // same copy wherever they hit this.
      const block = getTextBlockReason({ patient: this.patient, timezone: this.timezone || '' });
      if (block.blocked) return block.message;
      if (this.patientPhoneLooksInvalid) return "The phone number on file for this patient doesn't look like a valid number — check it in Open Dental.";
      if (!this.hasPatientPhone) return 'No phone number on file for this patient';
      return '';
    },
    emailDisabledReason() {
      if (!this.siteEmailAvailable) return "Email sending isn't set up for your practice — use Copy link instead.";
      if (!this.hasPatientEmail) return 'No email address on file for this patient';
      return '';
    },
    /** True when the only way to reach this patient is a manual paste. */
    copyOnly() { return !this.siteSmsAvailable && !this.siteEmailAvailable; },
    /** Same 'there' fallback the SMS/email template substitution uses. */
    patientFirstName() { return (this.patient && this.patient.fName) || 'there'; },

    practiceName() {
      const fromSettings = (this.settingsGeneralResolved && this.settingsGeneralResolved.practiceName) || '';
      if (fromSettings.trim()) return fromSettings;
      const sites = (this.$store.state.customer && this.$store.state.customer.sites) || [];
      const site = sites.find((s) => s.SiteID === this.$store.state.customer.siteID);
      return (site && site.Name) || 'Your Dental Office';
    },
    smsTemplateRaw() {
      return (this.settingsDeliveryResolved && this.settingsDeliveryResolved.smsTemplate) || '';
    },
    emailSubjectRaw() {
      return (this.settingsDeliveryResolved && this.settingsDeliveryResolved.emailSubject) || '';
    },
    emailBodyRaw() {
      return (this.settingsDeliveryResolved && this.settingsDeliveryResolved.emailBody) || '';
    },
    smsBodyResolved() {
      // Live preview of substituted message — what the patient will actually receive
      const template = this.smsCustomMessage || this.smsTemplateRaw;
      const fName = (this.patient && this.patient.fName) || 'there';
      return String(template || '')
        .replace(/\{patientFirstName\}/g, fName)
        .replace(/\{practiceName\}/g, this.practiceName)
        .replace(/\{planLink\}/g, this.fullUrl || '');
    },

    // ─── Slice 11 Phase B — Consent forms bundling ───
    /**
     * Practice has at least one usable mapping (codes + a form picked).
     * Hides the entire consent section when no mappings exist so a TC at a
     * practice that hasn't configured consent forms doesn't see noise.
     */
    hasConsentMappingsConfigured() {
      // Forms entitlement gates this too, not just the Settings → Consent Forms tab.
      // Disabling that tab only prevents NEW mappings; a practice configured while
      // Forms was on (or set up by support) keeps its saved mappings in
      // TX_PRESENTER_SETTINGS_CONSENT, and this checkbox reads that blob — so
      // without this condition the toggle stays live on a non-Forms site. The
      // server default-denies as well (lib/entitlements.js).
      if (!this.formsPaidFor) return false;
      const mappings = (this.settingsConsentResolved && this.settingsConsentResolved.mappings) || [];
      return mappings.some((m) =>
        Array.isArray(m.codes) && m.codes.length > 0
        && Array.isArray(m.formIDs) && m.formIDs.length > 0,
      );
    },
    /**
     * Build a Map<codeString, Set<formIDForWeb>> from the practice's mappings.
     * Iterating once here keeps the per-procedure resolution below O(n).
     */
    consentMappingIndex() {
      const idx = new Map();
      const mappings = (this.settingsConsentResolved && this.settingsConsentResolved.mappings) || [];
      mappings.forEach((m) => {
        if (!Array.isArray(m.codes) || !Array.isArray(m.formIDs)) return;
        m.codes.forEach((code) => {
          const key = String(code);
          if (!idx.has(key)) idx.set(key, new Set());
          m.formIDs.forEach((fid) => {
            if (typeof fid === 'string' && fid.trim()) idx.get(key).add(fid);
          });
        });
      });
      return idx;
    },
    /**
     * Procedures from the Builder. Mirrors what the patient view will see —
     * we resolve consent against ALL procs on the plan, including alts and
     * hidden ones, since the consent forms are about clinical procedures
     * regardless of which alt the patient ultimately picks.
     */
    planProcedures() {
      return (this.builder && Array.isArray(this.builder.procedures)) ? this.builder.procedures : [];
    },

    // ─── Photo Attachments ───────────────────────────────────────────────
    /**
     * The plan's effective showPhotos, through the same three-level resolver
     * the Builder and Present Mode use. Reading the raw annotation instead
     * would show OFF for every plan predating the feature, since the key is
     * simply absent there — "unset" is not "false".
     */
    resolvedShowPhotos() {
      const pd = (this.builder && this.builder.annotations && this.builder.annotations.pricingDisplay) || null;
      const defaults = ((this.settingsPresentationResolved || {}).displayDefaults) || {};
      return resolvePricingDisplay(pd, defaults).showPhotos;
    },
    patientPhotoCount() {
      return this.$store.state.txPresenter.patientPhotos.list.length;
    },
    /** Exactly what the patient will see — same filter as every other surface. */
    planPhotos() {
      return filterPhotosForPlan(
        this.$store.state.txPresenter.patientPhotos.list,
        planToothNums(this.planProcedures),
      );
    },
    /**
     * Resolved list of consent forms to bundle: dedup'd by formIDForWeb.
     * The form names come from the consent settings load (Slice 11 Phase A
     * already fetched them) — but those aren't stored on the mapping itself.
     * Fallback "Consent form" label keeps the chip readable; the patient
     * view shows the real name from the backend lookup.
     */
    resolvedConsentForms() {
      const idx = this.consentMappingIndex;
      const seen = new Set();
      const out = [];
      this.planProcedures.forEach((p) => {
        const key = String(p.codeNum);
        const forms = idx.get(key);
        if (!forms) return;
        forms.forEach((fid) => {
          if (seen.has(fid)) return;
          seen.add(fid);
          out.push({ formIDForWeb: fid, formName: this.formNameLookup(fid) });
        });
      });
      return out;
    },
    /**
     * Codes on the plan that have NO mapping. Drives the warning banner so
     * the TC knows which procedures will lack a consent form.
     */
    unmappedCodes() {
      const idx = this.consentMappingIndex;
      const seen = new Set();
      const out = [];
      this.planProcedures.forEach((p) => {
        const key = String(p.codeNum);
        const display = p.procCode || key;
        if (idx.has(key)) return;
        if (seen.has(display)) return;
        seen.add(display);
        out.push(display);
      });
      return out;
    },
    unmappedCodesPreview() {
      // Cap the display at 4 codes so the banner doesn't grow unbounded.
      if (this.unmappedCodes.length <= 4) return this.unmappedCodes.join(', ');
      return this.unmappedCodes.slice(0, 4).join(', ') + '…';
    },
  },
  watch: {
    patient: {
      immediate: true,
      handler(p) {
        if (p) {
          // Only seed the box with something actually dialable. Pre-filling the raw OD
          // column meant a field reading "DO NOT TEXT" was offered as the recipient and,
          // if the TC sent anyway, went straight to Twilio as the `recipient` override —
          // bypassing any patient-record check. An empty box is the honest prompt.
          const digits = String(p.wirelessPhone || '').replace(/\D/g, '');
          this.smsRecipient = digits.length >= 10 ? p.wirelessPhone : '';
          this.emailRecipient = p.email || '';
          // Slice 10a #5 — auto-default channel selection to whichever
          // contact field exists. If neither exists, default to 'copy'.
          // Skip if TC already picked a channel they can use (don't
          // override their selection mid-session).
          //
          // Now also honors SITE entitlement: without it a Core-plan office opened
          // this modal already sitting on a disabled SMS tile with no obvious next
          // step. They should land on Copy link, which is their actual workflow.
          const canSms = this.canSendSms;
          const canEmail = this.canSendEmail;
          const cur = this.activeChannel;
          const curUsable = (cur === 'sms' && canSms)
            || (cur === 'email' && canEmail)
            || cur === 'copy';
          if (!curUsable) {
            if (canEmail) this.activeChannel = 'email';
            else if (canSms) this.activeChannel = 'sms';
            else this.activeChannel = 'copy';
          }
        }
      },
    },
    // Slice 11 Phase B — load form names for chip preview when modal opens
    // (only if the practice has consent mappings configured, otherwise the
    // section is hidden and the fetch is wasted).
    'sendPlan.open': {
      immediate: true,
      handler(isOpen) {
        if (isOpen && this.hasConsentMappingsConfigured && this.consentFormsList.length === 0) {
          this.loadConsentFormsList();
        }
        // Photo Attachments — the Builder and Present Mode already loaded the
        // library, and the store caches per patient so those are no-ops. This
        // is here for the Patient Profile mount, which reaches the Send modal
        // without ever opening the Builder.
        if (isOpen && this.builder.siteID && this.builder.patNum) {
          this.$store.dispatch('txPresenter/loadPatientPhotos', {
            siteID: this.builder.siteID,
            patNum: this.builder.patNum,
          });
        }
      },
    },
    // Slice 11 Phase B — when the modal first hydrates the persisted bundle
    // (openSendPlan returns it from createTxShortUrl), seed the toggle so it
    // reflects what's actually saved on the row. Reopening the modal for a
    // plan with an existing bundle now shows toggle=ON without the TC having
    // to re-tick. Saves only happen on explicit @change events, so this seed
    // does NOT fire a redundant save back to the backend.
    'sendPlan.persistedBundledFormIDs': {
      immediate: true,
      handler(ids) {
        this.bundleConsentForms = Array.isArray(ids) && ids.length > 0;
      },
    },
    // Photo Attachments — seed the checkbox from the plan's RESOLVED showPhotos
    // (per-plan → practice → hardcoded), same as the Builder toggle. Seeding
    // only, no save: an immediate handler that wrote back would persist a
    // per-plan `true` onto every plan the TC merely opened this modal for,
    // quietly freezing it against a later practice-default change.
    resolvedShowPhotos: {
      immediate: true,
      handler(v) {
        this.includePhotos = v;
      },
    },
  },
  beforeDestroy() {
    if (this.copyTimer) clearTimeout(this.copyTimer);
  },
  methods: {
    ...mapActions('txPresenter', ['closeSendPlan', 'sendTxLink', 'saveTxBundledForms']),
    onClose() {
      this.closeSendPlan();
    },
    onSelectAll(e) { e.target.select(); },

    /**
     * Flip the plan's showPhotos flag from the Send modal.
     *
     * Goes through the store's setPricingDisplayFlag rather than a local
     * whitelist: this modal mounts under the Builder, Present Mode AND the
     * Patient Profile, and a fourth copy of the pricingDisplay merge is
     * exactly the shape of bug that shipped "Total only" broken.
     *
     * Reverts the checkbox on failure, matching onToggleBundleConsentForms —
     * a checkbox that stays ticked after a failed save is a promise the plan
     * won't keep.
     */
    async onToggleIncludePhotos() {
      const wantedOn = this.includePhotos;
      this.photosSaving = true;
      this.photosSaveError = null;
      try {
        await this.$store.dispatch('txPresenter/setPricingDisplayFlag', {
          key: 'showPhotos',
          value: wantedOn,
        });
      } catch (err) {
        console.error('[SendPlanModal] showPhotos save failed:', err);
        this.includePhotos = !wantedOn;
        this.photosSaveError = extractApiError(err, "Couldn't save the photo setting. Try again.");
      } finally {
        this.photosSaving = false;
      }
    },
    // Shared SMS guidance (<SmsGuidance>): block the SMS send when the resolved body
    // is over the carrier limit. Folded into the Send SMS button's :disabled.
    // Also stash the payload so onSend() can gate a costly (warn-state) send on cost confirm.
    onSmsGuidance(g) {
      this.smsBlocked = !!(g && g.blocked);
      this.lastGuidance = g || null;
    },
    // Insert a {placeholder} token into the SMS/email message box at the caret.
    // Mirrors the cursor-splice in TaskList/vueat/AtTextarea.vue. Invoked via
    // @mousedown.prevent so the textarea keeps focus + selection (no caret jump).
    insertVar(token, refName, modelKey) {
      const ta = this.$refs[refName];
      const val = this[modelKey] || '';
      const focused = !!ta && document.activeElement === ta;
      const start = focused ? ta.selectionStart : val.length;
      const end = focused ? ta.selectionEnd : val.length;
      this[modelKey] = val.slice(0, start) + token + val.slice(end);
      const pos = start + token.length;
      this.$nextTick(() => {
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      });
    },
    async onCopy() {
      if (!this.fullUrl) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.fullUrl);
        } else if (this.$refs.urlInput) {
          this.$refs.urlInput.select();
          document.execCommand('copy');
        }
        this.copied = true;
        if (this.copyTimer) clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { this.copied = false; }, 1800);
      } catch (e) {
        console.error('Copy failed:', e);
      }
    },
    async onSend(channel) {
      this.sending = true;
      this.sendError = null;
      try {
        const payload = { channel };
        if (channel === 'sms') {
          payload.recipient = this.smsRecipient;
          payload.clientNonce = this.attemptNonce;
          if (this.smsCustomMessage.trim()) payload.customMessage = this.smsCustomMessage.trim();
        } else if (channel === 'email') {
          payload.recipient = this.emailRecipient;
          if (this.emailCustomSubject.trim()) payload.customSubject = this.emailCustomSubject.trim();
          if (this.emailCustomBody.trim()) payload.customBody = this.emailCustomBody.trim();
        }
        // Slice 11 Phase B — bundle consent forms when toggle is on AND we
        // resolved at least one. Empty array would clear any prior selection
        // — fine, since a TC explicitly leaving the toggle off should reset.
        if (this.bundleConsentForms) {
          payload.bundledFormIDs = this.resolvedConsentForms.map((f) => f.formIDForWeb);
        } else {
          payload.bundledFormIDs = [];
        }
        const result = await this.sendTxLink(payload);
        this.sentResult = result;
        this.attemptNonce = newAttemptNonce();
      } catch (e) {
        // Was `e.response?.data?.message || e.message`, which could never work:
        // /opt/common serialises error bodies as a BARE JSON STRING, so `.data.message`
        // is always undefined and this fell through to axios's "Request failed with
        // status code 500" on every failure — the string a TC at Tulip Tree re-clicked
        // against five times on 2026-08-04. extractApiError understands all three real
        // body shapes, and passes through the message on the Errors our store throws
        // (including the quiet-200 non-delivery copy).
        this.sendError = extractApiError(e, 'Failed to send. Please try again.');
      } finally {
        this.sending = false;
      }
    },

    // ─── Slice 11 Phase B — Consent forms ───
    /**
     * One-shot fetch of the practice's "OTHER" forms so the chip preview
     * shows real names ("Crown Consent" rather than the formIDForWeb UUID).
     * Same endpoint SettingsConsent.vue uses; we don't share state because
     * the modal can open without ever visiting Settings.
     */
    async loadConsentFormsList() {
      const siteID = this.builder && this.builder.siteID;
      if (!siteID) return;
      try {
        const response = await axios.post('/get-forms-for-consent-mapping', { siteID }, { crossdomain: true });
        if (response.data && response.data.success) {
          this.consentFormsList = response.data.data.forms || [];
        }
      } catch (e) {
        // Non-fatal — chips fall back to "Consent form" when the lookup fails.
        console.warn('[SendPlanModal] consent forms list load failed:', e.message);
      }
    },
    formNameLookup(formIDForWeb) {
      const hit = this.consentFormsList.find((f) => f.formIDForWeb === formIDForWeb);
      return (hit && hit.formName) || 'Consent form';
    },
    goToConsentSettings() {
      this.closeSendPlan();
      this.$router.push({ name: 'TxPresenterSettings', query: { section: 'consent' } });
    },
    /**
     * Slice 11 Phase B — explicit toggle handler (replaces the earlier watcher
     * approach). Seeding the toggle from persisted state was triggering the
     * watcher and causing a redundant save back to the same value. Doing it
     * via @change ensures the backend round-trip only happens on user
     * intent.
     */
    onToggleBundleConsentForms() {
      const ids = this.bundleConsentForms
        ? this.resolvedConsentForms.map((f) => f.formIDForWeb)
        : [];
      const wantedOn = this.bundleConsentForms;
      this.bundleSaving = true;
      this.bundleSaveError = null;
      this.saveTxBundledForms({ bundledFormIDs: ids })
        .catch((e) => {
          // Slice 10a #11 — surface the save error so the TC can retry.
          // Without this, a network blip silently leaves the toggle in
          // the user-set state while the backend has the old state;
          // patient view then shows the wrong bundle.
          console.warn('[SendPlanModal] bundle save failed:', e.message);
          // Revert the local toggle to its persisted value so the UI
          // reflects what the backend actually has.
          this.bundleConsentForms = !wantedOn;
          this.bundleSaveError = (e && e.message)
            ? `Could not save consent forms toggle: ${e.message}. Try again.`
            : 'Could not save consent forms toggle. Try again.';
        })
        .finally(() => { this.bundleSaving = false; });
    },
  },
};
</script>

<style scoped>
.spm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.spm-modal {
  background: #fff;
  width: 100%;
  max-width: 520px;
  /* Cap to the viewport (backdrop adds 28px padding top+bottom) so a tall modal
     never grows past the top edge; the body scrolls instead — see .spm-body. */
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.spm-modal * { box-sizing: border-box; }

.spm-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e4e7ec;
}
.spm-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}
.spm-title {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
  margin-top: 4px;
}
.spm-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  cursor: pointer;
}
.spm-close:hover { background: #f1f3f6; }

.spm-body {
  padding: 16px 22px;
  /* Scroll the body (header stays pinned) when content exceeds the capped modal. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.spm-error {
  padding: 8px 12px;
  border-radius: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 12.5px;
  margin-bottom: 12px;
}

.spm-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 24px;
  justify-content: center;
  color: #475569;
  font-size: 13px;
}

.spm-section { margin-bottom: 18px; }
.spm-section:last-child { margin-bottom: 0; }
.spm-section-label {
  font-size: 10.5px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-weight: 600;
  margin-bottom: 8px;
}

.spm-link-row {
  display: flex;
  gap: 6px;
  align-items: stretch;
}
.spm-link-input {
  flex: 1;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #f7f8fa;
  color: #0f172a;
}
.spm-link-input:focus { outline: none; border-color: #0d5f54; }

.spm-btn,
.spm-btn-primary {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.spm-btn:hover { background: #f1f3f6; }
.spm-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.spm-btn-primary:hover { background: #094840; }
.spm-btn-success { background: #10b981 !important; border-color: #10b981 !important; }

.spm-channels {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.spm-channel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 10px;
  border-radius: 10px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.spm-channel:hover:not(:disabled) {
  background: #ecf5f3;
  border-color: #b9dcd5;
  color: #0d5f54;
}
.spm-channel:disabled { opacity: 0.5; cursor: not-allowed; }
.spm-channel .fa { font-size: 18px; color: #0d5f54; margin-bottom: 2px; }
.spm-channel-name {
  font-size: 13px;
  font-weight: 600;
}
.spm-channel-sub {
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.spm-preview-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #0d5f54;
  text-decoration: none;
  font-weight: 500;
}
.spm-preview-link:hover { text-decoration: underline; }

.spm-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin: 6px 0 0;
  line-height: 1.5;
}

/* Copy-only practice: this is their actual send path, so it reads as guidance
   (teal, like the rest of the primary affordances) rather than a muted footnote. */
.spm-hint-copyonly {
  color: #0d5f54;
  background: #e6f1ef;
  border-radius: 6px;
  padding: 7px 10px;
  margin-top: 8px;
}
.spm-hint-copyonly .fa { margin-right: 5px; }

/* Sender disclosure for platform-sender practices. Informational, not a warning —
   this is the site's normal, correct send path, so it reads like the copy-only
   guidance above rather than an error. Muted enough not to compete with the fields,
   present enough that nobody sends without having seen it. */
.spm-sender-note {
  font-size: 11.5px;
  color: #0d5f54;
  background: #e6f1ef;
  border-radius: 6px;
  padding: 7px 10px;
  margin: 0 0 12px;
  line-height: 1.5;
  word-break: break-word;
}
.spm-sender-note .fa { margin-right: 5px; }
.spm-sender-note strong { font-weight: 600; }

.spm-footer {
  padding: 12px 22px;
  border-top: 1px solid #e4e7ec;
  display: flex;
  justify-content: flex-end;
  background: #fafafa;
}

/* Slice 7e — channel forms */
.spm-channel-active {
  background: #ecf5f3 !important;
  border-color: #0d5f54 !important;
  color: #094840 !important;
}

.spm-field { margin-bottom: 12px; }
.spm-field:last-child { margin-bottom: 0; }
.spm-field > label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #475569;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 5px;
}
.spm-input,
.spm-textarea {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.spm-textarea {
  resize: vertical;
  min-height: 60px;
  line-height: 1.5;
}
.spm-input:focus,
.spm-textarea:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.spm-charcount {
  margin-left: auto;
  font-size: 10.5px;
  color: #94a3b8;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
.spm-charcount.warn { color: #b45309; }

.spm-btn-block {
  display: flex !important;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
  padding: 10px;
  font-size: 14px;
}

.spm-hint code {
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
  font-size: 11px;
  background: #f1f3f6;
  padding: 1px 4px;
  border-radius: 3px;
  color: #0d5f54;
}
/* Clickable placeholder chips — same look as .spm-hint code, but a real button
   that inserts the token at the caret. */
.spm-var-chip {
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
  font-size: 11px;
  background: #f1f3f6;
  padding: 1px 5px;
  margin: 0 1px;
  border: 1px solid #e4e7ec;
  border-radius: 3px;
  color: #0d5f54;
  cursor: pointer;
  line-height: 1.7;
  transition: background 0.12s, border-color 0.12s;
}
.spm-var-chip:hover:not(:disabled) {
  background: #e6f1ef;
  border-color: #0d5f54;
}
.spm-var-chip:disabled { opacity: 0.5; cursor: not-allowed; }

.spm-sent {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border: 1px solid #b9dcd5;
  background: #ecf5f3;
  border-radius: 10px;
  margin-bottom: 16px;
}
.spm-sent .fa { font-size: 28px; color: #10b981; flex-shrink: 0; }
.spm-sent-title { font-size: 15px; font-weight: 600; color: #094840; }
.spm-sent-meta { font-size: 12px; color: #475569; margin-top: 2px; word-break: break-all; }
/* "Already sent": informational, not the green success check. */
.spm-sent-info { border-color: #bfd3ea; background: #eff5fb; }
.spm-sent-info .fa { color: #3b6ea8; }
.spm-sent-info .spm-sent-title { color: #1e3a5f; }

/* Slice 11 Phase B — consent forms bundling */
.spm-consent-toggle {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  cursor: pointer;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f7f8fa;
  transition: background 0.1s, border-color 0.1s;
}
.spm-consent-toggle:hover { background: #eef2f6; }
.spm-consent-toggle input[type='checkbox'] {
  margin: 2px 0 0;
  width: 14px;
  height: 14px;
  accent-color: #0d5f54;
  cursor: pointer;
  flex-shrink: 0;
}
.spm-consent-label {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.4;
}

.spm-consent-preview {
  margin-top: 10px;
  padding: 10px 12px;
  background: #ecf5f3;
  border: 1px solid #b9dcd5;
  border-radius: 7px;
}
.spm-consent-preview-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #094840;
  font-weight: 600;
  margin-bottom: 6px;
}
.spm-consent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.spm-consent-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #b9dcd5;
  color: #094840;
  font-size: 12px;
  font-weight: 500;
}
.spm-consent-chip .fa { font-size: 11px; color: #0d5f54; }

/* Photo Attachments — the pre-send preview strip. Scrolls horizontally rather
   than wrapping so it can never push the Send button below the fold. */
.spm-photo-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}
.spm-photo-item {
  flex: 0 0 auto;
  width: 84px;
}
.spm-photo-thumb {
  width: 84px;
  height: 64px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #b9dcd5;
  background: #f1f5f9;
  display: block;
}
.spm-photo-teeth {
  margin-top: 3px;
  font-size: 10.5px;
  font-weight: 600;
  color: #094840;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.spm-photo-teeth-any {
  color: #7c8a99;
  font-weight: 500;
  font-style: italic;
}

.spm-consent-empty {
  margin-top: 10px;
  padding: 9px 12px;
  background: #fef9f0;
  border: 1px solid #f1d8b1;
  border-radius: 7px;
  color: #78350f;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.5;
}
.spm-consent-empty .fa { color: #b45309; margin-top: 1px; }

.spm-consent-warn {
  margin-top: 10px;
  padding: 9px 12px;
  background: #fef9f0;
  border: 1px solid #f1d8b1;
  border-radius: 7px;
  color: #78350f;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.5;
}
.spm-consent-warn .fa { color: #b45309; margin-top: 1px; flex-shrink: 0; }
.spm-consent-warn a {
  color: #0d5f54;
  font-weight: 500;
  text-decoration: underline;
}
.spm-consent-warn a:hover { color: #094840; }

/* Slice 10a #11 — bundle save error banner + spinner-in-label */
.spm-consent-saving {
  margin-left: 6px;
  font-size: 11px;
  color: #94a3b8;
}
.spm-consent-error {
  margin-top: 10px;
  padding: 9px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 7px;
  color: #b91c1c;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.5;
}
.spm-consent-error > .fa { color: #b91c1c; margin-top: 1px; flex-shrink: 0; }
.spm-consent-error-dismiss {
  margin-left: auto;
  border: none;
  background: transparent;
  color: #b91c1c;
  cursor: pointer;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
}
.spm-consent-error-dismiss:hover { color: #7f1d1d; }
.spm-consent-error-dismiss .fa { font-size: 11px; }
</style>
